/**
 * Dashboard API Service
 * Fetches dashboard data from the backend
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': token ? `Bearer ${token}` : ''
  };
};

/**
 * Fetch dashboard summary
 */
export const fetchDashboardSummary = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/summary`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch dashboard summary');
  }
  
  return await response.json();
};

/**
 * Fetch user's books
 */
export const fetchUserBooks = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/books`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch books');
  return await response.json();
};

/**
 * Fetch user's people context
 */
export const fetchUserPeople = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/people`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch people');
  return await response.json();
};

/**
 * Create a new person manually
 */
export const createPerson = async (personData) => {
  const response = await fetch(`${API_URL}/api/dashboard/people`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(personData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create person');
  }
  return await response.json();
};

/**
 * Update a person's details
 */
export const updatePerson = async (alias, updates) => {
  const response = await fetch(`${API_URL}/api/dashboard/people/${encodeURIComponent(alias)}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update person');
  }
  return await response.json();
};

/**
 * Merge two people (source into target)
 */
export const mergePeople = async (sourceAlias, targetAlias) => {
  const response = await fetch(`${API_URL}/api/dashboard/people/merge`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      source_alias: sourceAlias,
      target_alias: targetAlias
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to merge people');
  }
  return await response.json();
};

/**
 * Delete a person
 */
export const deletePerson = async (alias) => {
  const response = await fetch(`${API_URL}/api/dashboard/people/${encodeURIComponent(alias)}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to delete person');
  }
  return await response.json();
};

/**
 * Fetch person details
 */
export const fetchPersonDetail = async (alias) => {
  const response = await fetch(`${API_URL}/api/dashboard/people/${encodeURIComponent(alias)}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch person details');
  return await response.json();
};

/**
 * Fetch user's reflections with optional filters
 * @param {object} opts - { limit, offset, person, theme, channel, since, includeArchived }
 */
export const fetchUserReflections = async (limitOrOpts = 20, offset = 0) => {
  // Backwards-compatible: accept (limit, offset) OR a single opts object
  let opts;
  if (typeof limitOrOpts === 'object' && limitOrOpts !== null) {
    opts = limitOrOpts;
  } else {
    opts = { limit: limitOrOpts, offset };
  }
  const params = new URLSearchParams();
  params.set('limit', opts.limit ?? 20);
  params.set('offset', opts.offset ?? 0);
  if (opts.person && opts.person.length) params.set('person', Array.isArray(opts.person) ? opts.person.join(',') : opts.person);
  if (opts.theme && opts.theme.length) params.set('theme', Array.isArray(opts.theme) ? opts.theme.join(',') : opts.theme);
  if (opts.channel && opts.channel.length) params.set('channel', Array.isArray(opts.channel) ? opts.channel.join(',') : opts.channel);
  if (opts.since) params.set('since', opts.since);
  if (opts.includeArchived) params.set('include_archived', 'true');

  const response = await fetch(`${API_URL}/api/dashboard/reflections?${params.toString()}`, {
    headers: getAuthHeaders()
  });

  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch reflections');
  }
  return await response.json();
};

/**
 * Fetch available filter options for reflections (people, themes, channels)
 */
export const fetchReflectionFilterOptions = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/reflections/filter-options`, {
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch filter options');
  }
  return await response.json();
};

/**
 * Consolidate (merge) multiple conversations into a single thread
 */
export const consolidateReflections = async (conversationIds, title = null) => {
  const response = await fetch(`${API_URL}/api/dashboard/conversations/consolidate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      conversation_ids: conversationIds,
      title: title
    })
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to consolidate reflections');
  }
  return await response.json();
};

/**
 * Unmerge a consolidated conversation, restoring the originals
 */
export const unmergeReflection = async (mergedId) => {
  const response = await fetch(`${API_URL}/api/dashboard/conversations/${encodeURIComponent(mergedId)}/unmerge`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || 'Failed to unmerge reflection');
  }
  return await response.json();
};

/**
 * Fetch reflection details
 */
export const fetchReflectionDetail = async (conversationId) => {
  const response = await fetch(`${API_URL}/api/dashboard/reflections/${conversationId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch reflection details');
  return await response.json();
};

/**
 * Fetch user's patterns
 */
export const fetchUserPatterns = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/patterns`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch patterns');
  return await response.json();
};

/**
 * Fetch pending follow-ups
 */
export const fetchFollowups = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/followups`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch follow-ups');
  return await response.json();
};

/**
 * Dismiss a follow-up
 */
export const dismissFollowup = async (conversationId) => {
  const response = await fetch(`${API_URL}/api/dashboard/followups/${conversationId}/dismiss`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to dismiss follow-up');
  return await response.json();
};

/**
 * Update person alias
 */
export const updatePersonAlias = async (oldAlias, newAlias) => {
  const response = await fetch(`${API_URL}/api/dashboard/people/${encodeURIComponent(oldAlias)}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify({ new_alias: newAlias })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update alias');
  }
  return await response.json();
};

/**
 * Generate 1-1 prep notes for a person
 */
export const generatePrepNotes = async (alias) => {
  const response = await fetch(`${API_URL}/api/dashboard/people/${encodeURIComponent(alias)}/prep-notes`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to generate prep notes');
  }
  return await response.json();
};

/**
 * Email prep notes to user
 */
export const emailPrepNotes = async (alias, prepNotes) => {
  const response = await fetch(`${API_URL}/api/dashboard/people/${encodeURIComponent(alias)}/prep-notes/email`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ prep_notes: prepNotes })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to send email');
  }
  return await response.json();
};

/**
 * Mark an action as completed or incomplete
 */
export const updateActionStatus = async (actionId, completed) => {
  const response = await fetch(`${API_URL}/api/companion/web/actions/${actionId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify({ completed })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update action');
  }
  return await response.json();
};

/**
 * Fetch user's actions
 */
export const fetchUserActions = async (includeCompleted = false) => {
  const response = await fetch(`${API_URL}/api/companion/web/actions?include_completed=${includeCompleted}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch actions');
  return await response.json();
};

/**
 * Fetch actions for a specific conversation
 */
export const fetchConversationActions = async (conversationId) => {
  const response = await fetch(`${API_URL}/api/companion/web/actions/conversation/${conversationId}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch conversation actions');
  return await response.json();
};

// --- Accomplishments ---

/**
 * Fetch pending accomplishment suggestions (also triggers a scan)
 */
export const fetchAccomplishmentSuggestions = async () => {
  const response = await fetch(`${API_URL}/api/accomplishments/suggestions`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch suggestions');
  return await response.json();
};

/**
 * Accept an accomplishment suggestion as-is
 */
export const acceptSuggestion = async (suggestionId) => {
  const response = await fetch(`${API_URL}/api/accomplishments/suggestions/${suggestionId}/accept`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to accept suggestion');
  return await response.json();
};

/**
 * Edit and accept a suggestion
 */
export const editAcceptSuggestion = async (suggestionId, edits) => {
  const response = await fetch(`${API_URL}/api/accomplishments/suggestions/${suggestionId}/edit-accept`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(edits)
  });
  
  if (!response.ok) throw new Error('Failed to edit suggestion');
  return await response.json();
};

/**
 * Dismiss an accomplishment suggestion
 */
export const dismissSuggestion = async (suggestionId) => {
  const response = await fetch(`${API_URL}/api/accomplishments/suggestions/${suggestionId}/dismiss`, {
    method: 'POST',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to dismiss suggestion');
  return await response.json();
};

/**
 * Fetch confirmed accomplishments
 */
export const fetchAccomplishments = async () => {
  const response = await fetch(`${API_URL}/api/accomplishments`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch accomplishments');
  return await response.json();
};

/**
 * Fetch accomplishment stats for dashboard
 */
export const fetchAccomplishmentStats = async () => {
  const response = await fetch(`${API_URL}/api/accomplishments/stats`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) throw new Error('Failed to fetch stats');
  return await response.json();
};


// =============================================
// Projects API Functions
// =============================================

/**
 * Fetch all projects for current user
 */
export const fetchUserProjects = async (status = null) => {
  let url = `${API_URL}/api/dashboard/projects`;
  if (status) {
    url += `?status=${status}`;
  }
  
  const response = await fetch(url, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch projects');
  }
  return await response.json();
};

/**
 * Fetch project stats for dashboard
 */
export const fetchProjectStats = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/projects/stats`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch project stats');
  }
  return await response.json();
};

/**
 * Fetch a single project by alias
 */
export const fetchProjectDetail = async (alias) => {
  const response = await fetch(`${API_URL}/api/dashboard/projects/${encodeURIComponent(alias)}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    if (response.status === 404) throw new Error('Project not found');
    throw new Error('Failed to fetch project');
  }
  return await response.json();
};

/**
 * Create a new project
 */
export const createProject = async (projectData) => {
  const response = await fetch(`${API_URL}/api/dashboard/projects`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(projectData)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to create project');
  }
  return await response.json();
};

/**
 * Update an existing project
 */
export const updateProject = async (alias, updates) => {
  const response = await fetch(`${API_URL}/api/dashboard/projects/${encodeURIComponent(alias)}`, {
    method: 'PUT',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(updates)
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to update project');
  }
  return await response.json();
};

/**
 * Archive (soft delete) a project
 */
export const archiveProject = async (alias) => {
  const response = await fetch(`${API_URL}/api/dashboard/projects/${encodeURIComponent(alias)}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) throw new Error('Project not found');
    throw new Error('Failed to archive project');
  }
  return await response.json();
};

/**
 * Fetch reflections related to a project
 */
export const fetchProjectReflections = async (alias, limit = 20) => {
  const response = await fetch(`${API_URL}/api/dashboard/projects/${encodeURIComponent(alias)}/reflections?limit=${limit}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch project reflections');
  }
  return await response.json();
};

/**
 * Add a person to a project
 */
export const addPersonToProject = async (projectAlias, personAlias, role = 'contributor') => {
  const response = await fetch(`${API_URL}/api/dashboard/projects/${encodeURIComponent(projectAlias)}/people`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ person_alias: personAlias, role })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to add person to project');
  }
  return await response.json();
};


// ============================================================
// WORKSPACE & ENTITY RELATIONSHIPS (W0)
// ============================================================

/**
 * Get workspace summary with counts and recent activity
 */
export const fetchWorkspaceSummary = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/workspace/summary`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 401) throw new Error('Unauthorized');
    throw new Error('Failed to fetch workspace summary');
  }
  return await response.json();
};

/**
 * Get person with all their relationships
 */
export const fetchPersonWithRelationships = async (alias) => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/person/${encodeURIComponent(alias)}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error('Failed to fetch person relationships');
  }
  return await response.json();
};

/**
 * Get project with all its relationships
 */
export const fetchProjectWithRelationships = async (alias) => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/project/${encodeURIComponent(alias)}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error('Failed to fetch project relationships');
  }
  return await response.json();
};

/**
 * Get accomplishment with all its relationships
 */
export const fetchAccomplishmentWithRelationships = async (accomplishmentId) => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/accomplishment/${encodeURIComponent(accomplishmentId)}`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error('Failed to fetch accomplishment relationships');
  }
  return await response.json();
};

/**
 * Link an accomplishment to a project
 */
export const linkAccomplishmentToProject = async (accomplishmentId, projectAlias) => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/accomplishment-project`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      accomplishment_id: accomplishmentId,
      project_alias: projectAlias
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to link accomplishment to project');
  }
  return await response.json();
};

/**
 * Link an accomplishment to people
 */
export const linkAccomplishmentToPeople = async (accomplishmentId, peopleAliases) => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/accomplishment-people`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      accomplishment_id: accomplishmentId,
      people_aliases: peopleAliases
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to link accomplishment to people');
  }
  return await response.json();
};

/**
 * Link a person to a project with a role
 */
export const linkPersonToProject = async (personAlias, projectAlias, role = 'contributor') => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/project-person`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      project_alias: projectAlias,
      person_alias: personAlias,
      role: role
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to link person to project');
  }
  return await response.json();
};

/**
 * Unlink a person from a project
 */
export const unlinkPersonFromProject = async (personAlias, projectAlias) => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/project-person?project_alias=${encodeURIComponent(projectAlias)}&person_alias=${encodeURIComponent(personAlias)}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to unlink person from project');
  }
  return await response.json();
};

/**
 * Link capability domains to a project
 */
export const linkProjectToCapabilities = async (projectAlias, capabilityDomains) => {
  const response = await fetch(`${API_URL}/api/dashboard/relationships/project-capabilities`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      project_alias: projectAlias,
      capability_domains: capabilityDomains
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Failed to link capabilities to project');
  }
  return await response.json();
};

/**
 * Infer relationships from existing reflections
 */
export const inferRelationships = async () => {
  const response = await fetch(`${API_URL}/api/dashboard/workspace/infer-relationships`, {
    headers: getAuthHeaders()
  });
  
  if (!response.ok) {
    throw new Error('Failed to infer relationships');
  }
  return await response.json();
};
